import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, ProtectedRoute } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ProposalGenerator from './pages/ProposalGenerator';
import PublicGrantMonitoring from './pages/PublicGrantMonitoring';
import ProposalManagement from './pages/ProposalManagement';
import CompanyDataPool from './pages/CompanyDataPool';
import MatchingConsortium from './pages/MatchingConsortium';
import VirtualEvaluation from './pages/VirtualEvaluation';
import PPTGenerator from './pages/PPTGenerator';
import Settings from './pages/Settings';
import AdminPanel from './pages/AdminPanel';
import CompanyRegistration from './pages/CompanyRegistration';
import RegionalKnowhow from './pages/RegionalKnowhow';
import DemoGuide from './pages/DemoGuide';
import Portfolio from './pages/Portfolio';
import Projects from './pages/Projects';
import ProjectWorkspace from './pages/ProjectWorkspace';
import Analytics from './pages/Analytics';
import Collaboration from './pages/Collaboration';
import Templates from './pages/Templates';

// OAuth 콜백 처리 컴포넌트
const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // AuthContext의 useEffect에서 이미 토큰을 처리하므로
    // 잠시 후 대시보드로 리다이렉트
    const timer = setTimeout(() => {
      navigate('/', { replace: true });
    }, 1500);
    
    return () => clearTimeout(timer);
  }, [navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">로그인 처리 중...</p>
      </div>
    </div>
  );
};

function App() {
  return (
    <SettingsProvider>
      <AuthProvider>
        <div className="App">
          <Routes>
            {/* 공개 라우트 */}
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/google/callback" element={<AuthCallback />} />
            
            {/* 보호된 라우트 */}
            <Route path="/" element={<Layout />}>
              <Route index element={<Dashboard />} />
              <Route path="proposal-generator" element={
                <ProtectedRoute minimumRole="manager">
                  <ProposalGenerator />
                </ProtectedRoute>
              } />
              <Route path="grant-monitoring" element={<PublicGrantMonitoring />} />
              <Route path="proposals" element={<ProposalManagement />} />
              <Route path="company-pool" element={<CompanyDataPool />} />
              <Route path="company-registration" element={<CompanyRegistration />} />
              <Route path="regional-knowhow" element={<RegionalKnowhow />} />
              <Route path="matching" element={
                <ProtectedRoute minimumRole="manager">
                  <MatchingConsortium />
                </ProtectedRoute>
              } />
              <Route path="evaluation" element={<VirtualEvaluation />} />
              <Route path="ppt-generator" element={
                <ProtectedRoute minimumRole="manager">
                  <PPTGenerator />
                </ProtectedRoute>
              } />
              <Route path="portfolio" element={<Portfolio />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:projectId" element={<ProjectWorkspace />} />
              <Route path="analytics" element={
                <ProtectedRoute minimumRole="manager">
                  <Analytics />
                </ProtectedRoute>
              } />
              <Route path="collaboration" element={
                <ProtectedRoute minimumRole="manager">
                  <Collaboration />
                </ProtectedRoute>
              } />
              <Route path="templates" element={<Templates />} />
              <Route path="settings" element={<Settings />} />
              <Route path="demo-guide" element={<DemoGuide />} />
              <Route path="admin" element={
                <ProtectedRoute minimumRole="admin">
                  <AdminPanel />
                </ProtectedRoute>
              } />
            </Route>
            
            {/* 404 리다이렉트 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </AuthProvider>
    </SettingsProvider>
  );
}

export default App; 